import React, { useMemo } from "react"
import { MenuProps } from "antd"
import {
  faEdit,
  faSync,
  faExchangeAlt,
  faTrash,
} from "@fortawesome/free-solid-svg-icons"

import useContextMenu from "features/Reporting/hooks/UI/useContextMenu"

import { createMenuItem } from "utils/contextMenuHelpers"

/**
 * Props for useTableContextMenu hook
 */
export interface UseTableContextMenuProps {
  /** Whether table has data */
  hasData: boolean
  /** Whether in show selects mode */
  showSelects: boolean
  /** Loading state */
  isLoading: boolean
  /** Queued for execution state */
  isQueuedForExecution: boolean
  /** Whether table is transposed */
  isTransposed: boolean
  /** Whether delete is available */
  onDeleteRequest?: () => void
  /** Edit handler */
  onEdit: () => void
  /** Refresh handler */
  onRefresh: () => void
  /** Delete handler */
  onDelete: () => void
  /** Toggle transpose handler */
  onToggleTranspose: () => void
}

/**
 * Hook for table context menu functionality
 *
 * Provides context menu items and handlers for table elements.
 * Handles main context menu with table-specific operations.
 *
 * @param props - Hook configuration
 * @returns Context menu handlers and components
 */
export const useTableContextMenu = ({
  hasData,
  showSelects,
  isLoading,
  isQueuedForExecution,
  isTransposed,
  onDeleteRequest,
  onEdit,
  onRefresh,
  onDelete,
  onToggleTranspose,
}: UseTableContextMenuProps) => {
  const contextMenuItems = useMemo((): MenuProps["items"] => {
    const items: MenuProps["items"] = []

    if (!showSelects) {
      items.push(
        createMenuItem("refresh", faSync, "Refresh Table", onRefresh, {
          disabled: isLoading || isQueuedForExecution,
        }),
        createMenuItem("edit", faEdit, "Edit Variables", onEdit),
        createMenuItem(
          "transpose",
          faExchangeAlt,
          isTransposed ? "Remove Transpose" : "Transpose Table",
          onToggleTranspose
        )
      )
    } else {
      items.push(createMenuItem("edit", faEdit, "Reset Variables", onEdit))
    }

    if (onDeleteRequest) {
      items.push(
        { type: "divider", key: "div-delete" },
        createMenuItem("delete", faTrash, "Delete Table", onDelete, {
          danger: true,
        })
      )
    }

    return items
  }, [
    showSelects,
    isLoading,
    isQueuedForExecution,
    isTransposed,
    onRefresh,
    onEdit,
    onToggleTranspose,
    onDeleteRequest,
    onDelete,
  ])

  // Context menu hook
  const { handleContextMenu, ContextMenu } = useContextMenu({
    menuItems: contextMenuItems,
    isEnabled: !!(
      (!showSelects && hasData) ||
      (showSelects && onDeleteRequest)
    ),
    baseClassName: "table-context-menu",
  })

  return {
    handleContextMenu,
    ContextMenu,
  }
}

/**
 * Component wrapper that uses the hook and returns React elements
 */
const TableContextMenu: React.FC<UseTableContextMenuProps> = (props) => {
  const { ContextMenu } = useTableContextMenu(props)
  return <>{ContextMenu}</>
}

export default TableContextMenu
